import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });
// Preview company path (/c/{slug}): selects the company for public/seller pages only; staff access always comes from the server.
api.interceptors.request.use((cfg) => {
  const slug = sessionStorage.getItem("company_path");
  if (slug) cfg.headers["X-Company-Slug"] = slug;
  return cfg;
});

export const requestLink = (email, name, next) =>
  api.post("/auth/request-link", { email, name, next }).then((r) => r.data);
export const BACKGROUND = { headers: { "X-Background": "1" } };
export const devAvailable = () => api.get("/auth/dev-available").then((r) => r.data);
export const devLogin = () => api.post("/auth/dev-login").then((r) => r.data);

export const getHorses = (recordType, mode = "live") => api.get("/horses", { params: { ...(recordType ? { record_type: recordType } : {}), mode } }).then((r) => r.data);
export const getMyHorses = () => api.get("/horses", { params: { mine: true } }).then((r) => r.data);
export const setRecordType = (ids, record_type) => api.put("/admin/horses/record-type", { ids, record_type }).then((r) => r.data);
export const getHorsesMeta = () => api.get("/horses/meta").then((r) => r.data);
export const getHorse = (id) => api.get(`/horses/${id}`).then((r) => r.data);
export const getPreview = (id) => api.get(`/horses/${id}/preview`).then((r) => r.data);
export const syncSmartsheet = () => api.post("/sync").then((r) => r.data);
export const generateWriteups = (id, body = {}) =>
  api.post(`/horses/${id}/generate-writeups`, body).then((r) => r.data);

export const mediaRawUrl = (id, attachmentId, rotation) =>
  `${API}/horses/${id}/media/${attachmentId}/raw${rotation ? `?r=${rotation}` : ""}`;
export const rotateMedia = (id, attachmentId, direction) => api.post(`/horses/${id}/media/${attachmentId}/rotate`, { direction }).then((r) => r.data);
export const setMediaOrder = (id, orders) =>
  api.post(`/horses/${id}/media-order`, { orders }).then((r) => r.data);
export const uploadAdPhoto = (id, file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/horses/${id}/photos`, fd, { onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0) }).then((r) => r.data);
};
export const removeAdPhoto = (id, attId) => api.delete(`/horses/${id}/photos/${attId}`).then((r) => r.data);
export const deletePaper = (id, mid) => api.delete(`/horses/${id}/papers/${mid}`).then((r) => r.data);
export const scanPaper = (id, mid) => api.post(`/horses/${id}/papers/${mid}/scan`).then((r) => r.data);
export const requestPaperReview = (id, mid) => api.post(`/horses/${id}/papers/${mid}/request-review`).then((r) => r.data);
export const replacePaper = (id, mid, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/horses/${id}/papers/${mid}/replace`, fd).then((r) => r.data);
};
export const prefillHorse = (id) => api.post(`/horses/${id}/prefill`).then((r) => r.data);
export const getSaleSchedule = (id) => api.get(`/horses/${id}/sale-schedule`).then((r) => r.data);
export const setSellerSaleDates = (id, body) => api.post(`/horses/${id}/sale-dates`, body).then((r) => r.data);
export const suggestHeadlines = (id) => api.post(`/horses/${id}/headlines`).then((r) => r.data.headlines);
export const listPapers = (id) => api.get(`/horses/${id}/papers`).then((r) => r.data);
export const paperUrl = (id, mid) => `${API}/horses/${id}/papers/${mid}/file`;
export const uploadPaper = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/horses/${id}/papers`, fd).then((r) => r.data);
};
export const suggestMedia = (id) =>
  api.post(`/horses/${id}/suggest-media`).then((r) => r.data);

export const listStyleExamples = () => api.get("/style-examples").then((r) => r.data);
export const createStyleExample = (body) => api.post("/style-examples", body).then((r) => r.data);
export const updateStyleExample = (id, body) => api.put(`/style-examples/${id}`, body).then((r) => r.data);
export const deleteStyleExample = (id) => api.delete(`/style-examples/${id}`).then((r) => r.data);

export const getSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (body) => api.put("/settings", body).then((r) => r.data);

export const createHorse = (body) => api.post("/horses", body).then((r) => r.data);
export const updateHorse = (id, body) => api.patch(`/horses/${id}`, body).then((r) => r.data);
export const submitListing = (id) => api.post(`/horses/${id}/submit`).then((r) => r.data);
export const getReviewQueue = () => api.get("/review-queue").then((r) => r.data);
export const requestChanges = (id, note) =>
  api.post(`/horses/${id}/request-changes`, { note }).then((r) => r.data);
export const approveListing = (id) => api.post(`/horses/${id}/approve`).then((r) => r.data);
export const scheduleListing = (id) => api.post(`/horses/${id}/schedule`).then((r) => r.data);
export const unpublishListing = (id) => api.post(`/horses/${id}/unpublish`).then((r) => r.data);
export const publishListing = (id, now = false) => api.post(`/horses/${id}/publish`, null, { params: now ? { now: true } : {} }).then((r) => r.data);
export const setWorkflowStatus = (id, body) =>
  api.put(`/horses/${id}/workflow-status`, body).then((r) => r.data);
export const updatePublishing = (id, dest, body) =>
  api.put(`/horses/${id}/publishing/${dest}`, body).then((r) => r.data);
export const assignSeller = (id, email) =>
  api.post(`/horses/${id}/assign-seller`, { email }).then((r) => r.data);

// Seller Preview Mode (admin-only, isolated test drafts)
export const startSellerPreview = (horseId) =>
  api.post(`/seller-preview/start/${horseId}`).then((r) => r.data);
export const listSellerDrafts = () => api.get("/seller-preview/drafts").then((r) => r.data);
export const deleteSellerDraft = (id) => api.delete(`/seller-preview/drafts/${id}`).then((r) => r.data);
export const getContractTemplate = (draftId) => api.get("/contract-template", { params: draftId ? { draft_id: draftId } : {} }).then((r) => r.data);
export const signContract = (id, body) =>
  api.post(`/seller-preview/${id}/sign`, body).then((r) => r.data);
export const contractPdfUrl = (id) => `${API}/seller-preview/${id}/contract.pdf`;
export const getSaleDates = () => api.get("/sale-dates").then((r) => r.data);
